import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, FormikHelpers } from 'formik';
import { Button, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ApplicationJobCreate from './ApplicationJobCreate';
import { MyApplicationDetail } from '../../api/openapi';
import { restValidationToForm, setYupLocale } from '../../utils/formUtils';
import { applicationApi } from '../../api';

interface Props {
  item: MyApplicationDetail;
  onSaveSuccess: () => void;
}

interface Values {
  job: {
    job: string;
    company: string;
    description: string | undefined;
  }[];
}

const ApplicationJobsCreate: React.FC<Props> = ({ item, onSaveSuccess }: Props) => {
  const { t, i18n } = useTranslation(['viewApplication', 'common']);
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null);

  useEffect(() => {
    setYupLocale(Yup, i18n.language);
    setValidationSchema(
      Yup.object().shape({
        job: Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string(),
              job: Yup.string().label(t('job')).required(),
              company: Yup.string().label(t('jobCompany')).required(),
            })
          )
          .label(t('job'))
          .required(),
      })
    );
  }, [t, i18n.language]);

  return (
    <>
      <Formik<Values>
        initialValues={{
          job: item.job.map((e) => ({
            job: e.job,
            company: e.company,
            description: e.description,
          })),
        }}
        validateOnBlur={false}
        // validateOnChange={false}
        validationSchema={validationSchema}
        // enableReinitialize
        onSubmit={async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
          try {
            await applicationApi.myApplicationPartialUpdate({
              uuid: item.id,
              patchedMyApplicationDetailRequest: {
                job: values.job.map((e) => ({
                  job: e.job,
                  company: e.company,
                  description: e.description,
                })),
              },
            });

            toast.success(t('applicationUpdate'));

            onSaveSuccess();
          } catch (e) {
            restValidationToForm({
              t,
              errorResponse: e.response,
              setFieldError,
              errorFieldMap: {
                job: 'job',
                company: 'company',
                description: 'description',
              },
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, touched, errors, getFieldProps, setFieldValue, handleReset, setStatus, handleSubmit }): React.ReactElement => (
          <Form
            noValidate
            onReset={handleReset}
            onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
              setStatus('submitted');
              handleSubmit(e);
            }}
          >
            <Form.Row>
              <Col md="4">
                <p className="small text-muted">{t('jobHelp')}</p>
              </Col>
              <Col md="8">
                {' '}
                <Form.Group>
                  {values.job.map((_, i) => (
                    <ApplicationJobCreate key={`job-${i}`} index={i} />
                  ))}

                  <Button
                    type="button"
                    variant="secondary"
                    className="float-right"
                    onClick={(e) =>
                      setFieldValue('job', [
                        ...values.job,
                        {
                          job: '',
                          company: '',
                          description: '',
                        },
                      ])
                    }
                  >
                    <FontAwesomeIcon icon={faPlusCircle} /> {t('addJob')}
                  </Button>
                </Form.Group>
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setStatus('submitted');
                    handleSubmit();
                  }}
                >
                  {t('common:save')}
                </Button>
              </Col>
            </Form.Row>
          </Form>
        )}
      </Formik>
    </>
  );
};
export default ApplicationJobsCreate;
